import { cn } from '@/ui/utils'
import { useScrollToTop } from '@react-navigation/native'
import { Image, type ImageSource } from 'expo-image'
import type { ReactNode } from 'react'
import { useRef } from 'react'
import { ScrollView, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { pageShellStyles } from './page-shell.styles'

interface PageShellProps {
	/** Fills the right half on desktop; below `lg` the page is the content column alone. */
	photo?: ImageSource
	/** The content column scrolls by default. Off for screens that own their own scrolling. */
	scroll?: boolean
	/** No top padding, so a photo or a colour block can sit flush under the header. */
	flush?: boolean
	contentClassName?: string
	children: ReactNode
}

/**
 * The deck's page: one content column, and on a wide screen a brand photo beside it.
 *
 * Owns the only scroll view on the page, so it belongs inside a `Screen` with the `fixed`
 * preset: under a scrolling preset the header and the photo would scroll away with the copy.
 */
export const PageShell = ({
	photo,
	scroll = true,
	flush = false,
	contentClassName,
	children,
}: PageShellProps) => {
	const styles = pageShellStyles()
	// Tapping the current tab again brings the page back to the top, as the system's tabs do.
	const scrollRef = useRef<ScrollView>(null)
	useScrollToTop(scrollRef)
	const insets = useSafeAreaInsets()
	const content = cn(styles.content(), flush && 'pt-0 lg:pt-0', contentClassName)

	return (
		<View className={styles.row()}>
			<View className={styles.column()}>
				{scroll ? (
					<ScrollView
						ref={scrollRef}
						keyboardShouldPersistTaps="handled"
						showsVerticalScrollIndicator={false}
						contentContainerClassName={content}
						// The last thing on a page clears the home indicator.
						contentContainerStyle={{ paddingBottom: insets.bottom + 32 }}
					>
						{children}
					</ScrollView>
				) : (
					<View className={cn('flex-1', content)}>{children}</View>
				)}
			</View>
			{photo && (
				<View className={styles.photoPane()}>
					<Image source={photo} style={{ flex: 1 }} contentFit="cover" />
				</View>
			)}
		</View>
	)
}
